import React, { useCallback } from 'react';
import { Draggable } from 'react-beautiful-dnd';
import styles from './DragAndDropStarredBoardsListItem.less';
import { BoardTile } from 'app/src/components/BoardTile';
import ReactDOM from '@trello/react-dom-wrapper';
import { Analytics } from '@trello/atlassian-analytics';
import { StarredBoardsMenuTestIds } from '@trello/test-ids';

export const STARRED_BOARDS_MENU_POPOVER_CLASSNAME =
  'starred-boards-menu-popover';

export interface HiddenStarredBoardsListItemProps {
  boardId: string;
  index: number;
}

export const HiddenStarredBoardsListItem: React.FC<HiddenStarredBoardsListItemProps> = ({
  boardId,
  index,
}) => {
  return (
    <Draggable key={boardId} draggableId={boardId} index={index} isDragDisabled>
      {({ innerRef }) => {
        return (
          <li key={boardId} className={styles.hiddenListItem} ref={innerRef} />
        );
      }}
    </Draggable>
  );
};

interface BackgroundImageScaledProps {
  readonly width: number;
  readonly height: number;
  readonly url: string;
}

export interface Board {
  id: string;
  idBoardStar: string;
  pos: number;
  name: string;
  prefs?: {
    background?: string;
    backgroundTile?: boolean;
    backgroundImage?: string;
    backgroundColor?: string;
    backgroundImageScaled?: BackgroundImageScaledProps[];
    isTemplate?: boolean;
  };
  organization?: {
    displayName?: string;
  };
  dateLastView: Date | undefined;
  dateLastActivity: Date | undefined;
  url: string;
}

export interface DragAndDropStarredBoardsListItemProps {
  board: Board;
  disableDragAndDrop: boolean;
  index: number;
  maxBoardStarPosition: number;
  portal: Element | null;
}

export const DragAndDropStarredBoardsListItem: React.FC<DragAndDropStarredBoardsListItemProps> = ({
  board,
  disableDragAndDrop,
  index,
  maxBoardStarPosition,
  portal,
}) => {
  const {
    id,
    name,
    prefs,
    organization,
    dateLastView,
    dateLastActivity,
    idBoardStar,
    url,
  } = board;

  const handleBoardClick = useCallback((boardId?: string) => {
    Analytics.sendClickedLinkEvent({
      linkName: 'starredBoardsLink',
      source: 'starredBoardsMenuInlineDialog',
      containers: {
        board: {
          id: boardId,
        },
      },
    });
  }, []);

  const handleStarClick = useCallback((boardId?: string) => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'boardStarButton',
      source: 'starredBoardsMenuInlineDialog',
      attributes: {
        starred: false,
      },
      containers: {
        board: {
          id: boardId,
        },
      },
    });
  }, []);

  return (
    <Draggable
      key={id}
      draggableId={id}
      index={index}
      isDragDisabled={disableDragAndDrop}
    >
      {({ innerRef, draggableProps, dragHandleProps }, { isDragging }) => {
        const child = (
          <li
            key={id}
            ref={innerRef}
            {...draggableProps}
            style={{
              ...draggableProps.style,
            }}
            className={styles.listItem}
            data-test-id={
              StarredBoardsMenuTestIds.StarredBoardsMenuPopOverBoardTitle
            }
          >
            <BoardTile
              id={id}
              name={name}
              maxBoardStarPosition={maxBoardStarPosition}
              dateLastView={dateLastView}
              dateLastActivity={dateLastActivity}
              background={prefs?.background}
              backgroundTile={prefs?.backgroundTile}
              backgroundImage={prefs?.backgroundImage}
              backgroundColor={prefs?.backgroundColor}
              backgroundImageScaled={prefs?.backgroundImageScaled || undefined}
              url={url}
              organizationDisplayName={organization?.displayName}
              isTemplate={prefs?.isTemplate}
              boardStarId={idBoardStar}
              // eslint-disable-next-line react/jsx-no-bind
              onStarClick={() => handleStarClick(id)}
              // eslint-disable-next-line react/jsx-no-bind
              onBoardClick={() => handleBoardClick(id)}
              dragHandle={dragHandleProps}
            />
          </li>
        );

        if (!isDragging || !portal) {
          return child;
        } else {
          // if dragging - put the item in a portal
          // we need to create a portal here because this
          // list is rendered in a popover, with a parent
          // element that uses 'transform'. See this issue
          // for more detail: https://github.com/atlassian/react-beautiful-dnd/issues/128#issuecomment-333410772
          return ReactDOM.createPortal(child, portal);
        }
      }}
    </Draggable>
  );
};
